import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { describeError, errorCode } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { EnvBanner, ENV_BANNER_HEIGHT } from '../components/EnvBanner'

export function LoginPage() {
  const { user, login, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/'
    return <Navigate to={from} replace />
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setCode(undefined)
    setSubmitting(true)
    try {
      const signedIn = await login(username.trim(), password)
      navigate(signedIn.mustChangePassword ? '/profile' : '/', { replace: true })
    } catch (err) {
      setError(describeError(err))
      setCode(errorCode(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Before anyone signs in and starts entering a real day into a test system. */}
      <EnvBanner />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          // The banner is fixed to the viewport, so the card is padded down past it
          // rather than centring underneath it.
          pt: `${ENV_BANNER_HEIGHT}px`,
          background: 'linear-gradient(160deg, #0f3d5c 0%, #06263c 55%, #041a2b 100%)',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ textAlign: 'center', mb: 3, color: '#fff' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 1 }}>
            SEMS
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
            Site Execution Management System
          </Typography>
        </Box>

        <Card elevation={8}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Use the account issued by your administrator.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                {error}
                {code === 'ACCOUNT_LOCKED' && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Too many failed attempts. The lock clears automatically after 15 minutes.
                  </Typography>
                )}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
                autoFocus
                autoComplete="username"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete="current-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={submitting || !username || !password}
              >
                {submitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
          First run creates a `superadmin` account and prints its password to the backend log.
        </Typography>
        </Box>
      </Box>
    </>
  )
}
